/* 年月ピッカーカレンダー */
import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import moment from 'moment';
import Messages from '../molecules/Messages';
import { business_day_calendar as config } from '../Const';
import ja from '../lang/ja';
import DatePicker from 'react-datepicker';
import Paragraph from '../molecules/Paragraph';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';

interface Props {
  id: string;
  label: string;
  need?: boolean;
  value: Date | null;
  message?: string;
  name?: string;
  placeholder?: string;
  help_msg?: string;
  disabled: boolean;
  handleChange: (date: Date | null) => void;
  handleBlur?: VoidFunction;
  looks_inactive?: boolean;
  readonly?: boolean;
  suffix?: string;
}

const MonthPickerCalendar: React.FC<Props> = ({
  id,
  label,
  need,
  value,
  message,
  name,
  placeholder,
  help_msg,
  disabled,
  handleChange,
  handleBlur,
  looks_inactive,
  readonly,
  suffix,
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [enableMask, setEnableMask] = useState(true);
  // 開始日から終了日までの年のコレクションを作成する
  const years: number[] = [];
  const dateFrom = moment(new Date()).add(config.min_date, 'd');
  const dateTo = moment(new Date())
    .add(config.max_year, 'y')
    .subtract('1', 'd');

  for (let i = Number(dateFrom.format('YYYY')); i <= Number(dateTo.format('YYYY')); i++) {
    years.push(i);
  }

  /**
   * カレンダーアイコン押下時の処理
   * @return void
   */
  const handleCalendarIconClick = (): void => {
    // Note:アイコン押下時のみカレンダー表示
    //      カレンダークローズ時にフラグOFF
    setOpenDatePicker(true);
    const obj = document.getElementById(id);
    if (obj !== null) {
      obj.click();
    }
  };

  /**
   * カレンダークローズ時の処理
   * @return void
   */
  const handleCalendarClose = (): void => {
    (typeof handleBlur !== 'undefined') && handleBlur();
    setOpenDatePicker(false);
  };

  /**
   * DatePickerの上位要素のフォーカスアウト時の処理
   * @return void
   */
  const handleBlurTopLevelElement = (): void => {
    if (openDatePicker) {
      // カレンダー表示時のhandleBlurの２回発火抑止
      return;
    }

    const obj = document.getElementById(id) as HTMLInputElement;
    const inputDate = obj.value;
    if (inputDate !== '' &&
        inputDate.match(/^[0-9]{4}年(0[1-9]|1[0-2])月$/) === null) {
      // Note:無効な日付(入力が半端な状態)の場合は、
      //      入力Maskが有効になっていると、無効な日付が画面に残ってしまう
      //      そのため、入力Maskを無効化し、内部値もクリアさせる
      setEnableMask(false);
      handleChange(null);
    }

    (typeof handleBlur !== 'undefined') && handleBlur();
  };

  /**
   * 活性状態の時に出力するJSX.ELement
   * @returns JSX.Element
   */
  const enabledJsxElement = () => {
    return (
      <div className="standard-calendar" onBlur={handleBlurTopLevelElement}>
        <Row>
          <Col xs={9} sm={9} md={9} lg={9} xl={9}>
            <DatePicker
              id={id}
              className={`form-control ${message && 'textarea-error'}`}
              disabled={disabled || readonly}
              locale={ja}
              onChange={handleChange}
              selected={value ?? null}
              placeholderText={placeholder}
              name={name}
              dateFormat="yyyy年MM月"
              showPopperArrow={false}
              showMonthYearPicker
              autoComplete="off"
              maxDate={new Date('9999/12')}
              onCalendarClose={handleCalendarClose}
              onInputClick={() => {
                setEnableMask(true)
              }}
              open={openDatePicker}
              customInput={
                <MaskedInput
                  pipe={createAutoCorrectedDatePipe('yyyy/mm')}
                  mask={enableMask ? [/\d/, /\d/, /\d/, /\d/, '年', /\d/, /\d/, '月'] : []}
                  guide
                  type='tel'
                />
              }
            />
          </Col>
          {suffix && (
            <Col md={2} lg={2} xl={2} className={`calendar-mt px-0 suffix`}>
              {suffix}
            </Col>
          )}
          {!readonly && (
            <Col xs={3} sm={3} md={3} lg={3} xl={3}>
              <button className="calendar-open" onClick={handleCalendarIconClick}>
                <img src="/img/ico_009_pc.png" />
              </button>
            </Col>
          )}
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
      </div>
    );
  };

  /**
   * ラベル表示時のJSX.Elementを返却する
   * @returns JSX.Element
   */
  const returnLabelJsx = () => {
    if (value === null) {
      return <></>;
    }
    const date = suffix ? moment(value).format(`Y年M月${suffix}`) : moment(value).format('Y年M月');
    return <Paragraph label={date} />;
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={disabled || looks_inactive ? returnLabelJsx() : enabledJsxElement()}
    />
  );
};

export default MonthPickerCalendar;
